import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

type PSP = "mollie" | "stripe";

interface PayAtOrderSettings {
  psp: PSP;
  mollie_api_key: string;
  stripe_secret_key: string;
  stripe_publishable_key: string;
  test_mode: boolean;
  payment_expiry_minutes: number;
}

const DEFAULT_SETTINGS: PayAtOrderSettings = {
  psp: "mollie",
  mollie_api_key: "",
  stripe_secret_key: "",
  stripe_publishable_key: "",
  test_mode: true,
  payment_expiry_minutes: 30,
};

const PayAtOrderExtensionSettings = () => {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<PayAtOrderSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extensionId, setExtensionId] = useState<string | null>(null);
  const [showMollieKey, setShowMollieKey] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);

  const orgId = profile?.organisation_id;

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      const { data } = await supabase
        .from("organisation_extensions")
        .select("*")
        .eq("organisation_id", orgId)
        .eq("extension_id", "pay-at-order")
        .maybeSingle();
      if (data) {
        setExtensionId((data as any).id);
        const s = (data as any).settings as Partial<PayAtOrderSettings>;
        setSettings({ ...DEFAULT_SETTINGS, ...s });
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const save = async () => {
    if (!orgId) return;
    setSaving(true);
    if (extensionId) {
      await supabase
        .from("organisation_extensions")
        .update({ settings: settings as any })
        .eq("id", extensionId);
    } else {
      const { data } = await supabase
        .from("organisation_extensions")
        .insert({
          organisation_id: orgId,
          extension_id: "pay-at-order",
          enabled: true,
          settings: settings as any,
        })
        .select()
        .single();
      if (data) setExtensionId((data as any).id);
    }
    setSaving(false);
    toast({ title: t("ext.pao_saved") });
  };

  const hasRequiredKeys = settings.psp === "mollie"
    ? settings.mollie_api_key.length > 0
    : settings.stripe_secret_key.length > 0 && settings.stripe_publishable_key.length > 0;

  if (loading) return <div className="text-sm text-muted-foreground">{t("ext.loading")}</div>;

  return (
    <div>
      <h1 className="text-xl font-bold text-foreground mb-1">{t("ext.pao_title")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("ext.pao_subtitle")}</p>

      <div className="space-y-6 max-w-lg">
        {/* PSP Selection */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-card-foreground mb-1">{t("ext.pao_select_psp")}</h3>
          <p className="text-xs text-muted-foreground mb-4">{t("ext.pao_select_psp_desc")}</p>
          <RadioGroup
            value={settings.psp}
            onValueChange={(v) => setSettings((s) => ({ ...s, psp: v as PSP }))}
            className="space-y-3"
          >
            <label className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50 transition-colors has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
              <RadioGroupItem value="mollie" />
              <div className="flex-1">
                <p className="text-sm font-medium text-card-foreground">Mollie</p>
                <p className="text-xs text-muted-foreground">{t("ext.pao_mollie_desc")}</p>
              </div>
            </label>
            <label className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50 transition-colors has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
              <RadioGroupItem value="stripe" />
              <div className="flex-1">
                <p className="text-sm font-medium text-card-foreground">Stripe</p>
                <p className="text-xs text-muted-foreground">{t("ext.pao_stripe_desc")}</p>
              </div>
            </label>
          </RadioGroup>
        </div>

        {/* Test Mode */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-card-foreground">{t("ext.pao_test_mode")}</h3>
              <p className="text-xs text-muted-foreground">{t("ext.pao_test_mode_desc")}</p>
            </div>
            <Switch
              checked={settings.test_mode}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, test_mode: v }))}
            />
          </div>
        </div>

        {/* API Keys */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <h3 className="text-sm font-semibold text-card-foreground">{t("ext.pao_api_keys")}</h3>
          <p className="text-xs text-muted-foreground">{t("ext.pao_api_keys_desc")}</p>

          {settings.psp === "mollie" && (
            <div className="space-y-2">
              <Label className="text-xs">{t("ext.pao_mollie_api_key")}</Label>
              <div className="relative">
                <Input
                  type={showMollieKey ? "text" : "password"}
                  value={settings.mollie_api_key}
                  onChange={(e) => setSettings((s) => ({ ...s, mollie_api_key: e.target.value }))}
                  placeholder={settings.test_mode ? "test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" : "live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowMollieKey(!showMollieKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showMollieKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("ext.pao_mollie_key_hint")}
              </p>
            </div>
          )}

          {settings.psp === "stripe" && (
            <>
              <div className="space-y-2">
                <Label className="text-xs">{t("ext.pao_stripe_publishable_key")}</Label>
                <Input
                  type="text"
                  value={settings.stripe_publishable_key}
                  onChange={(e) => setSettings((s) => ({ ...s, stripe_publishable_key: e.target.value }))}
                  placeholder={settings.test_mode ? "pk_test_..." : "pk_live_..."}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{t("ext.pao_stripe_secret_key")}</Label>
                <div className="relative">
                  <Input
                    type={showStripeSecret ? "text" : "password"}
                    value={settings.stripe_secret_key}
                    onChange={(e) => setSettings((s) => ({ ...s, stripe_secret_key: e.target.value }))}
                    placeholder={settings.test_mode ? "sk_test_..." : "sk_live_..."}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeSecret(!showStripeSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showStripeSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("ext.pao_stripe_key_hint")}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Payment Expiry */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold text-card-foreground">{t("ext.pao_expiry_title")}</h3>
          <p className="text-xs text-muted-foreground">{t("ext.pao_expiry_desc")}</p>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              min={1}
              max={1440}
              value={settings.payment_expiry_minutes}
              onChange={(e) => setSettings((s) => ({ ...s, payment_expiry_minutes: Math.max(1, Math.min(1440, parseInt(e.target.value) || 30)) }))}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">{t("ext.pao_expiry_unit")}</span>
          </div>
        </div>

        {/* Warning if no keys */}
        {!hasRequiredKeys && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-xs text-destructive">{t("ext.pao_no_keys_warning")}</p>
          </div>
        )}

        <Button onClick={save} disabled={saving}>
          {saving ? t("ext.pao_saving") : t("ext.pao_save")}
        </Button>
      </div>
    </div>
  );
};

export default PayAtOrderExtensionSettings;
